import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { COMPANY, SERVICES } from "@/data/site";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[var(--ct-border)]"
    >
      <div className="section-container flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_premium-moving-wy/artifacts/63dddnb5_IMG_0277.webp"
            alt="Crosstown Movers Logo"
            className="w-11 h-11 rounded-lg object-contain bg-black"
          />
          <div className="leading-tight">
            <div className="font-bold text-[var(--ct-text)] tracking-tight" style={{ fontFamily: "Outfit" }}>
              Crosstown Movers
            </div>
            <div className="text-[10px] tracking-[0.2em] text-[var(--ct-text-muted)] uppercase">
              Wyoming · Since 1980
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <NavLink to="/" data-testid="nav-home" end className={({ isActive }) => `text-sm font-medium ${isActive ? 'text-[var(--ct-primary)]' : 'text-[var(--ct-text-muted)] hover:text-[var(--ct-primary)]'}`}>Home</NavLink>

          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              data-testid="nav-services"
              className="flex items-center gap-1 text-sm font-medium text-[var(--ct-text-muted)] hover:text-[var(--ct-primary)]"
            >
              Services <ChevronDown className="w-4 h-4" />
            </button>
            {servicesOpen && (
              <div
                data-testid="services-dropdown"
                className="absolute top-full left-0 pt-3 w-80"
              >
                <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.12)] border border-[var(--ct-border)] p-2">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`nav-service-${s.slug}`}
                      className="block px-4 py-3 rounded-xl hover:bg-[var(--ct-accent)] transition"
                    >
                      <div className="font-semibold text-sm text-[var(--ct-primary)]">{s.title}</div>
                      <div className="text-xs text-[var(--ct-text-muted)] mt-0.5">{s.seoTitle}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <NavLink to="/service-areas" data-testid="nav-areas" className={({ isActive }) => `text-sm font-medium ${isActive ? 'text-[var(--ct-primary)]' : 'text-[var(--ct-text-muted)] hover:text-[var(--ct-primary)]'}`}>Service Areas</NavLink>
          <NavLink to="/blog" data-testid="nav-blog" className={({ isActive }) => `text-sm font-medium ${isActive ? 'text-[var(--ct-primary)]' : 'text-[var(--ct-text-muted)] hover:text-[var(--ct-primary)]'}`}>Blog</NavLink>
          <NavLink to="/faq" data-testid="nav-faq" className={({ isActive }) => `text-sm font-medium ${isActive ? 'text-[var(--ct-primary)]' : 'text-[var(--ct-text-muted)] hover:text-[var(--ct-primary)]'}`}>FAQ</NavLink>
          <NavLink to="/about" data-testid="nav-about" className={({ isActive }) => `text-sm font-medium ${isActive ? 'text-[var(--ct-primary)]' : 'text-[var(--ct-text-muted)] hover:text-[var(--ct-primary)]'}`}>About</NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            data-testid="header-phone"
            href={`tel:${COMPANY.phoneTel}`}
            className="flex items-center gap-2 text-sm font-semibold text-[var(--ct-primary)] hover:text-[var(--ct-secondary)] transition"
          >
            <Phone className="w-4 h-4" />
            {COMPANY.phone}
          </a>
          <Link to="/#quote" data-testid="header-cta" className="btn-primary text-sm">
            Get Free Quote
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[var(--ct-border)] bg-white">
          <div className="section-container py-4 flex flex-col gap-1">
            <Link to="/" onClick={() => setOpen(false)} className="py-3 font-medium">Home</Link>
            <details className="group">
              <summary className="py-3 font-medium cursor-pointer flex items-center justify-between">
                Services <ChevronDown className="w-4 h-4 group-open:rotate-180 transition" />
              </summary>
              <div className="pl-4 pb-2">
                {SERVICES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    onClick={() => setOpen(false)}
                    className="block py-2 text-sm text-[var(--ct-text-muted)]"
                  >
                    {s.title}
                  </Link>
                ))}
              </div>
            </details>
            <Link to="/service-areas" onClick={() => setOpen(false)} className="py-3 font-medium">Service Areas</Link>
            <Link to="/blog" onClick={() => setOpen(false)} className="py-3 font-medium">Blog</Link>
            <Link to="/faq" onClick={() => setOpen(false)} className="py-3 font-medium">FAQ</Link>
            <Link to="/about" onClick={() => setOpen(false)} className="py-3 font-medium">About</Link>
            <a href={`tel:${COMPANY.phoneTel}`} className="py-3 font-semibold text-[var(--ct-secondary)]">
              {COMPANY.phone}
            </a>
            <Link to="/#quote" onClick={() => setOpen(false)} className="btn-primary mt-2 w-full text-sm">
              Get Free Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
